/**
 * Sort channels for the onboarding picker so the user's country
 * appears first. Mirrors the mobile priority sort at
 * `mobile/src/screens/onboarding/follow-channels-screen.tsx:94`.
 *
 * Priority order:
 *   0. channel.country === currentCountry
 *   1. channel.country === homeCountry
 *   2. everything else
 *
 * Stable within each bucket — keeps the API's existing order
 * (popularity/recency) for channels at the same priority.
 */
export interface RankableChannel {
  readonly id: string;
  readonly country: string | null;
}

export function rankChannelsByCountry<T extends RankableChannel>(
  channels: readonly T[],
  currentCountry: string | null | undefined,
  homeCountry: string | null | undefined,
): T[] {
  const current = currentCountry?.toUpperCase() ?? null;
  const home = homeCountry?.toUpperCase() ?? null;

  const priority = (c: T): number => {
    const country = c.country?.toUpperCase() ?? null;
    if (current && country === current) return 0;
    if (home && country === home) return 1;
    return 2;
  };

  // Use index as tiebreaker so the sort is stable (Array.prototype.sort
  // is stable in modern engines, but being explicit keeps the contract
  // obvious to future readers).
  return channels
    .map((c, i) => ({ c, i, p: priority(c) }))
    .sort((a, b) => a.p - b.p || a.i - b.i)
    .map(({ c }) => c);
}
